import { useEffect } from "react";
import siteSearchIndexImport from "../data/searchFiles/siteSearchIndex.js";

export const SITE_NAME = "Compass Through The Bible";

const siteSearchIndex = Array.isArray(siteSearchIndexImport)
  ? siteSearchIndexImport
  : siteSearchIndexImport?.default || [];

/*
 * The site search index already names every static route, so it doubles as the
 * source of truth for document titles instead of a second list to keep in sync.
 */
const routeTitles = new Map(
  siteSearchIndex
    .filter((item) => item?.route && item?.title)
    .map((item) => [item.route, item.title])
);

/*
 * Routes that carry a title but are not search destinations.
 */
const extraRouteTitles = new Map([
  ["/", null], // home renders the bare site name
  ["/search", "Search"],
  ["/my-study", "My Study"],
]);

function normalizePath(pathname = "/") {
  if (pathname.length > 1 && pathname.endsWith("/")) {
    return pathname.slice(0, -1);
  }

  return pathname;
}

/*
 * Composes the full document title. A null/empty page title yields the bare
 * site name, which is what the home page wants.
 */
export function formatDocumentTitle(pageTitle) {
  if (!pageTitle) return SITE_NAME;

  return `${pageTitle} — ${SITE_NAME}`;
}

/*
 * Resolves the page title for a static route, or null when the route is
 * dynamic (/people/:id, /battles/:id) and the page sets its own title.
 */
export function getRouteTitle(pathname) {
  const path = normalizePath(pathname);

  if (extraRouteTitles.has(path)) return extraRouteTitles.get(path);
  if (routeTitles.has(path)) return routeTitles.get(path);

  return null;
}

/*
 * Sets document.title on behalf of a page that knows its own name.
 *
 * Pass `undefined` to opt out (e.g. while a record is still loading) so the
 * current title is left alone rather than being cleared to the site name.
 *
 * It deliberately does not restore anything on unmount. It used to snapshot
 * document.title on mount and put it back on the way out, which meant a page
 * leaving clobbered whatever the page arriving had already set -- React runs
 * the departing cleanup after the arriving effect. Leaving /people/david for
 * /psalms restored "David"; going /judges -> /people/david -> /psalms restored
 * "Judges Explorer", a title belonging to neither page. Nothing needs the old
 * value: RouteMetadata titles every static route, and the pages that title
 * themselves all set it on mount.
 */
export function useDocumentTitle(pageTitle) {
  useEffect(() => {
    if (pageTitle === undefined) return;

    document.title = formatDocumentTitle(pageTitle);
  }, [pageTitle]);
}

export default useDocumentTitle;
